const isInViewport = (el, marginX = 0, marginY = 0) => {
	const rect = el.getBoundingClientRect()
	return (
		rect.bottom > -marginY &&
		rect.top < window.innerHeight + marginY &&
		rect.right > -marginX &&
		rect.left < window.innerWidth + marginX
	)
}

function createVideoController(videos) {
	const loaded = new WeakSet()
	const wantsPlay = new WeakSet()

	const loadVideo = (video, eager = false) => {
		if (loaded.has(video)) return
		const src = video.dataset.src
		if (!src) return
		video.preload = eager ? 'auto' : 'metadata'
		video.src = src
		video.load()
		loaded.add(video)
	}

	const attemptPlay = (video) => {
		if (!wantsPlay.has(video)) return

		const play = () => {
			if (!wantsPlay.has(video)) return
			video.play().catch(() => {})
		}

		if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
			play()
			return
		}

		video.addEventListener('loadeddata', play, { once: true })
		video.addEventListener('canplay', play, { once: true })
	}

	const playVideo = (video, eager = false) => {
		wantsPlay.add(video)
		loadVideo(video, eager)
		attemptPlay(video)
	}

	const pauseVideo = (video) => {
		wantsPlay.delete(video)
		video.pause()
	}

	return { playVideo, pauseVideo, loadVideo }
}

/**
 * Hero: vídeos visíveis (e colunas vizinhas) carregam na hora.
 * Colunas fora da tela só baixam quando aparecem. Pausa ao sair da viewport.
 */
export function initHeroVideos(selector = '.js-hero-video') {
	const videos = document.querySelectorAll(selector)
	if (!videos.length) return

	const isMobile = window.matchMedia('(max-width: 767px)').matches
	const marginX = isMobile ? window.innerWidth * 0.2 : window.innerWidth * 0.12
	const marginY = 120
	const { playVideo, pauseVideo } = createVideoController(videos)

	const syncVideo = (video, intersecting) => {
		if (intersecting) playVideo(video, true)
		else pauseVideo(video)
	}

	const observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				syncVideo(entry.target, entry.isIntersecting)
			}
		},
		{ rootMargin: `${marginY}px ${marginX}px`, threshold: 0 }
	)

	const bootstrap = () => {
		videos.forEach((video) => {
			observer.observe(video)
			if (isInViewport(video, marginX, marginY)) {
				playVideo(video, true)
			}
		})
	}

	bootstrap()

	let resizeTimer
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimer)
		resizeTimer = setTimeout(() => {
			videos.forEach((video) => {
				if (isInViewport(video, marginX, marginY)) playVideo(video, true)
			})
		}, 200)
	})

	return () => observer.disconnect()
}

/**
 * Vídeos fora do hero (ex.: CTA no rodapé) — lazy puro.
 */
export function initLazyVideos(selector = '.js-lazy-video') {
	const videos = document.querySelectorAll(selector)
	if (!videos.length) return

	const { playVideo, pauseVideo } = createVideoController(videos)

	const observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) playVideo(entry.target)
				else pauseVideo(entry.target)
			}
		},
		{ rootMargin: '80px 0px', threshold: 0 }
	)

	videos.forEach((video) => {
		observer.observe(video)
		if (isInViewport(video)) playVideo(video)
	})

	return () => observer.disconnect()
}
